/**
 * 011 T012: new — no vendor/isomorphic screen fits "type a reference, go straight to its record".
 * Every template list screen assumes a search endpoint; the Dispatcher's own `admin/invoices`
 * access is deliberately narrower than `invoices.read` (research.md R2), so this is not
 * `SearchInvoices` wired to a list, which would need a permission the Dispatcher doesn't hold and
 * a dispatch-status filter the billing service doesn't expose. One `Field` and one `Button`, the
 * same two primitives `Dispatch.tsx`'s own Part-B/cancel dialogs already compose.
 *
 * This is the Dispatcher's own navigation path to `/dispatch/:invoiceNumber` (FR-027, spec.md AC5):
 * the nav entry (`lib/nav.tsx`) points here instead of at a bare, unbuilt `/dispatch` href. The
 * route itself resolves what was typed — Billing's exact by-number lookup, which a Dispatcher may
 * call, or a legacy id it redirects to the number URL — and `Dispatch.tsx` renders its
 * `invoiceNotFound` EmptyState for a reference that doesn't resolve (T013), so a mistyped value
 * degrades to that screen rather than a crash or a 404 catch-all.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Field } from "@steelyard/theme";
import { Text, Button } from "rizzui";
import { dispatchPath } from "../../lib/paths";

export default function DispatchIndexPage() {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | undefined>();
  const navigate = useNavigate();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Enter an invoice reference.");
      return;
    }
    navigate(dispatchPath(trimmed));
  }

  return (
    <div className="flex max-w-md flex-col gap-6 @container">
      <PageHeader title="Dispatch" breadcrumb={[{ name: "Home", href: "/" }, { name: "Dispatch" }]} />
      <Text className="text-gray-500">Open the invoice you are dispatching against.</Text>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <Field
          label="Invoice reference"
          name="invoiceId"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            if (error) setError(undefined);
          }}
          hint="The invoice number on the paperwork."
          error={error}
          required
        />
        <Button type="submit" size="lg">
          Open
        </Button>
      </form>
    </div>
  );
}
